import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { List } from 'immutable';
import InfiniteScroll from 'react-infinite-scroll-component';
import { fetchQueueCallRecordsWithBPart } from 'flow/actions/pbx-actions';
import { fetchCallRecordsForExtension } from 'flow/actions/extension-actions';
import CallRecordSkeleton, {
    CallRecordItemSkeleton,
} from 'flow/components/loaders/CallRecordSkeleton';
import {
    CallRecordKeysByDate,
    CallRecordRow,
    callHistoryRowKey,
    callRecordKeyDates,
    rowIsFocusable,
} from 'flow/components/call-history-v2/call-history-rows';
import DateGroupedList, {
    DateGroupedRowProps,
} from 'flow/components/call-history-v2/DateGroupedList';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { olderQueueCallRecordsWithBPartAreAvailableInApi } from 'flow/selectors/queue-selectors';
import {
    makeSelectOwnCallRecordKeysByPhoneNumberNotActiveDateGroups,
    makeSelectQueueCallRecordKeysWithBPartNotActiveDateGroups,
    selectCallRecordFromKey,
    selectOwnCallRecordsNextBeforeForNumber,
} from 'flow/selectors/call-records-selectors';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';
import { PageType } from 'flow/components/call-history-v2/types';
import { CALL_RECORD_INCLUDES_RELATED_EXTENSION } from 'flow/utils/call-record-fetch-includes';
import RelatedCallRecordItem from './RelatedCallRecordItem';

/** The tab panel's scroller, when the page gives it no id of its own. */
const DEFAULT_SCROLLABLE_ID = 'main-scrollable-div';

// --- Queue-scoped related calls ---

interface QueueRelatedTabContentProps {
    callRecordKey: CallRecordKey;
    onNavigateToCallRecord?: (callRecordKey: CallRecordKey) => void;
    /** The id of the scroller that loads more calls at its end. */
    scrollableId?: string;
}

export const QueueRelatedTabContent = ({
    callRecordKey,
    onNavigateToCallRecord,
    scrollableId,
}: QueueRelatedTabContentProps) => {
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );
    const queueKey = callRecord?.getIn(['queue', 'key']);
    const callerNumber = callRecord?.getIn(['number', 'number']);

    if (!callerNumber || !queueKey) return null;

    return (
        <QueueRelatedContent
            queueKey={queueKey}
            callerNumber={callerNumber}
            excludeCallRecordKey={callRecordKey}
            onNavigateToCallRecord={onNavigateToCallRecord}
            scrollableId={scrollableId}
        />
    );
};

interface QueueRelatedContentProps {
    queueKey: QueueKey;
    callerNumber: string;
    excludeCallRecordKey?: CallRecordKey;
    onNavigateToCallRecord?: (callRecordKey: CallRecordKey) => void;
    scrollableId?: string;
}

const QueueRelatedContent = ({
    queueKey,
    callerNumber,
    excludeCallRecordKey,
    onNavigateToCallRecord,
    scrollableId,
}: QueueRelatedContentProps) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const handleItemClick = useCallback(
        (callRecordKey: CallRecordKey) => {
            if (onNavigateToCallRecord) {
                onNavigateToCallRecord(callRecordKey);
            } else {
                openModal('callRecordDetailV2', {
                    callRecordKey,
                    queueKey,
                    pageType: 'pbx',
                    segmentContext: 'PBX call detail',
                });
            }
        },
        [onNavigateToCallRecord, openModal, queueKey],
    );

    useEffect(() => {
        dispatch(fetchQueueCallRecordsWithBPart(queueKey, callerNumber));
    }, [dispatch, queueKey, callerNumber]);

    const initialLoading = useAppSelector(
        (state) =>
            state.callRecordsData.getIn([
                'sortingData',
                'queues',
                queueKey,
                'byPhoneNumber',
                callerNumber,
            ]) === undefined,
    );

    const selectGroupedKeys = useMemo(
        makeSelectQueueCallRecordKeysWithBPartNotActiveDateGroups,
        [],
    );
    const callRecordKeys = useAppSelector((state) =>
        selectGroupedKeys(state, queueKey, callerNumber),
    );

    const hasMore = useAppSelector((state) =>
        olderQueueCallRecordsWithBPartAreAvailableInApi(
            state,
            queueKey,
            callerNumber,
        ),
    );

    const recordCount =
        callRecordKeys?.reduce(
            (sum: number, group: List<CallRecordKey>) => sum + group.size,
            0,
        ) || 0;

    return (
        <InfiniteScroll
            className="overflow-visible!"
            next={() => {
                setIsLoadingMore(true);
                dispatch(
                    fetchQueueCallRecordsWithBPart(
                        queueKey,
                        callerNumber,
                        true,
                    ),
                ).finally(() => setIsLoadingMore(false));
            }}
            dataLength={recordCount}
            hasMore={!!hasMore}
            loader={isLoadingMore ? <CallRecordItemSkeleton /> : null}
            scrollableTarget={scrollableId ?? DEFAULT_SCROLLABLE_ID}
            endMessage={
                !initialLoading && recordCount > 0 ? (
                    <div className="flex justify-center py-9 text-sm text-gray-500">
                        {i18next.t('call.history.title.last')}
                    </div>
                ) : null
            }
        >
            {initialLoading ? (
                <CallRecordSkeleton />
            ) : (
                <RelatedCallRecordList
                    callRecordKeysByDate={callRecordKeys}
                    excludeCallRecordKey={excludeCallRecordKey}
                    queueKey={queueKey}
                    onItemClick={handleItemClick}
                    pageType="pbx"
                />
            )}
        </InfiniteScroll>
    );
};

// --- Personal call history related calls ---

interface PersonalRelatedTabContentProps {
    callRecordKey: CallRecordKey;
    /** The id of the scroller that loads more calls at its end. */
    scrollableId?: string;
}

export const PersonalRelatedTabContent = ({
    callRecordKey,
    scrollableId,
}: PersonalRelatedTabContentProps) => {
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );
    const callerNumber = callRecord?.getIn(['number', 'number']);
    if (!callerNumber) return null;

    return (
        <PersonalRelatedContent
            callerNumber={callerNumber}
            excludeCallRecordKey={callRecordKey}
            scrollableId={scrollableId}
        />
    );
};

const PersonalRelatedContent = ({
    callerNumber,
    excludeCallRecordKey,
    scrollableId,
}: {
    callerNumber: string;
    excludeCallRecordKey?: CallRecordKey;
    scrollableId?: string;
}) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const handleItemClick = useCallback(
        (callRecordKey: CallRecordKey) => {
            openModal('callRecordDetailV2', {
                callRecordKey,
                pageType: 'personal',
                segmentContext: 'Personal call detail',
            });
        },
        [openModal],
    );

    useEffect(() => {
        dispatch(
            fetchCallRecordsForExtension(
                ownExtensionKey,
                CALL_RECORD_INCLUDES_RELATED_EXTENSION,
                null,
                null,
                callerNumber,
            ),
        );
    }, [dispatch, ownExtensionKey, callerNumber]);

    const selectGroupedKeys = useMemo(
        makeSelectOwnCallRecordKeysByPhoneNumberNotActiveDateGroups,
        [],
    );
    const callRecordKeys = useAppSelector((state) =>
        selectGroupedKeys(state, callerNumber),
    );
    const initialLoading = callRecordKeys === undefined;

    const nextBefore = useAppSelector((state) =>
        selectOwnCallRecordsNextBeforeForNumber(state, callerNumber),
    );
    const hasMore = nextBefore !== null;

    const recordCount =
        callRecordKeys?.reduce(
            (sum: number, group: List<CallRecordKey>) => sum + group.size,
            0,
        ) || 0;

    return (
        <InfiniteScroll
            className="overflow-visible!"
            next={() => {
                setIsLoadingMore(true);
                dispatch(
                    fetchCallRecordsForExtension(
                        ownExtensionKey,
                        CALL_RECORD_INCLUDES_RELATED_EXTENSION,
                        null,
                        nextBefore,
                        callerNumber,
                    ),
                ).finally(() => setIsLoadingMore(false));
            }}
            dataLength={recordCount}
            hasMore={hasMore}
            loader={isLoadingMore ? <CallRecordItemSkeleton /> : null}
            scrollableTarget={scrollableId ?? DEFAULT_SCROLLABLE_ID}
            endMessage={
                !initialLoading && recordCount > 0 ? (
                    <div className="flex justify-center py-9 text-sm text-gray-500">
                        {i18next.t('call.history.title.last')}
                    </div>
                ) : null
            }
        >
            {initialLoading ? (
                <CallRecordSkeleton />
            ) : (
                <RelatedCallRecordList
                    callRecordKeysByDate={callRecordKeys}
                    excludeCallRecordKey={excludeCallRecordKey}
                    onItemClick={handleItemClick}
                    pageType="personal"
                />
            )}
        </InfiniteScroll>
    );
};

// --- Shared list rendering ---

interface RelatedCallRecordListProps {
    callRecordKeysByDate: CallRecordKeysByDate | undefined;
    /** The call whose page is open. It is not in the list. */
    excludeCallRecordKey?: CallRecordKey;
    queueKey?: QueueKey;
    /** Opens the call. Keep one function for the life of the list. */
    onItemClick: (callRecordKey: CallRecordKey) => void;
    pageType: PageType;
}

/**
 * The other calls from the same number by date, as one Tab stop. The open call
 * is not among them, so Tab enters at the first call until a call has had
 * focus. A call's name is a button that opens the call.
 */
export const RelatedCallRecordList = React.memo(function RelatedCallRecordList({
    callRecordKeysByDate,
    excludeCallRecordKey,
    queueKey,
    onItemClick,
    pageType,
}: RelatedCallRecordListProps) {
    const { i18next } = useAppContext();
    const dates = useMemo(
        () => callRecordKeyDates(callRecordKeysByDate, excludeCallRecordKey),
        [callRecordKeysByDate, excludeCallRecordKey],
    );

    const renderRow = (row: CallRecordRow, rowProps: DateGroupedRowProps) => (
        <RelatedCallRecordItem
            callRecordKey={row.callRecordKey}
            queueKey={queueKey}
            pageType={pageType}
            onOpen={onItemClick}
            {...rowProps}
        />
    );

    if (dates.length === 0) {
        return (
            <div className="flex justify-center py-9 text-sm text-gray-500">
                {i18next.t('call.history.related.empty')}
            </div>
        );
    }

    return (
        <DateGroupedList
            dates={dates}
            rowKey={callHistoryRowKey}
            rowIsFocusable={rowIsFocusable}
            renderRow={renderRow}
        />
    );
});
